import React, { useMemo, useState, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './SchedulingCalendar.css';

const localizer = momentLocalizer(moment);
const DragAndDropCalendar = withDragAndDrop(Calendar);

const formatTime12 = (date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

const CustomSlotSelection = ({ start, end }) => {
  const duration = Math.round((end - start) / (1000 * 60));
  const hours = Math.floor(duration / 60);
  const mins = duration % 60;
  const durationText = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;

  return (
    <div className="sched-cal__slotText">
      <div className="sched-cal__slotTime">
        {formatTime12(start)} - {formatTime12(end)}
      </div>
      <div className="sched-cal__slotDuration">({durationText})</div>
    </div>
  );
};

const SchedulingCalendarView = ({ estimates = [], onSelectSlot, selectedSlot = null }) => {
  const [dragSelection, setDragSelection] = useState(null);

  // When parent clears selection, clear local drag block too
  React.useEffect(() => {
    if (!selectedSlot) setDragSelection(null);
  }, [selectedSlot]);

  const events = useMemo(() => {
    return estimates
      .filter((e) => e.scheduled_date)
      .map((estimate) => {
        const start = new Date(estimate.scheduled_date);
        const durationMinutes = estimate.duration || 60;
        const end = new Date(start.getTime() + durationMinutes * 60000);

        return {
          id: estimate.id,
          title: `EST-${estimate.id}: ${estimate.customer_name}`,
          start,
          end,
          resource: estimate,
          isDragSelection: false,
        };
      });
  }, [estimates]);

  const handleSelectSlot = useCallback(
    (slotInfo) => {
      const durationMinutes = Math.round((slotInfo.end - slotInfo.start) / (1000 * 60));

      const range = { start: slotInfo.start, end: slotInfo.end };
      setDragSelection(range);

      onSelectSlot?.({ ...range, duration: durationMinutes });
    },
    [onSelectSlot]
  );

  const handleSelecting = useCallback((range) => {
    setDragSelection(range);
    return true;
  }, []);

  const handleEventResize = useCallback(
    ({ start, end }) => {
      const durationMinutes = Math.round((end - start) / (1000 * 60));
      const range = { start, end };
      setDragSelection(range);
      onSelectSlot?.({ ...range, duration: durationMinutes });
    },
    [onSelectSlot]
  );

  const handleEventDrop = useCallback(
    ({ event, start, end }) => {
      if (event.id !== 'drag-selection') return;

      const durationMinutes = Math.round((end - start) / (1000 * 60));
      const range = { start, end };
      setDragSelection(range);
      onSelectSlot?.({ ...range, duration: durationMinutes });
    },
    [onSelectSlot]
  );

  const allEvents = useMemo(() => {
    if (!dragSelection) return events;

    return [
      ...events,
      {
        id: 'drag-selection',
        title: '',
        start: dragSelection.start,
        end: dragSelection.end,
        isDragSelection: true,
      },
    ];
  }, [events, dragSelection]);

  const eventPropGetter = useCallback((event) => {
    return {
      className: event.isDragSelection ? 'sched-cal__event sched-cal__event--selection' : 'sched-cal__event',
    };
  }, []);

  const Event = useCallback(({ event, title }) => {
    if (event.isDragSelection) return <CustomSlotSelection start={event.start} end={event.end} />;
    return <div className="sched-cal__eventText">{title}</div>;
  }, []);

  // Custom formats for calendar display
  const formats = useMemo(() => ({
    weekdayFormat: (date, culture, localizer) => {
      // On mobile, show single letter; on desktop, show abbreviated
      if (window.innerWidth <= 768) {
        return localizer.format(date, 'dd', culture).charAt(0);
      }
      return localizer.format(date, 'ddd', culture);
    },
    dayHeaderFormat: (date, culture, localizer) =>
      localizer.format(date, 'ddd M/D', culture), // e.g., "Mon 1/6"
  }), []);

  return (
    <div className="sched-cal" style={{ height: '75vh', minHeight: '500px' }}>
      <DragAndDropCalendar
        localizer={localizer}
        events={allEvents}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%' }}
        views={['month', 'week', 'day']}
        defaultView="week"
        selectable
        resizable
        draggableAccessor={(event) => event.isDragSelection}
        onSelectSlot={handleSelectSlot}
        onSelecting={handleSelecting}
        onEventResize={handleEventResize}
        onEventDrop={handleEventDrop}
        step={30}
        timeslots={2}
        min={new Date(1970, 1, 1, 9, 0)}
        max={new Date(1970, 1, 1, 16, 0)}
        eventPropGetter={eventPropGetter}
        components={{ event: Event }}
        formats={formats}
      />
    </div>
  );
};

export default SchedulingCalendarView;
